import { money } from "@/lib/api";
import { EmptyState } from "@/components/shared";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Building2, Plus, Trash2, Pencil, Loader2, X, Rocket, Eye, EyeOff, CheckCircle2, ImagePlus, RotateCcw } from "lucide-react";
import { MigrateRentalDialog } from "@/components/MigrateRentalDialog";
import { ShareListingDialog } from "@/components/ShareListingDialog";

export const PropertiesTab = ({
  properties, payout, setActiveTab, propOpen, setPropOpen, openCreate, openEdit, editId,
  propForm, set, onImageFiles, removeImage, saveProperty, busy, deleteProperty, toggleVisibility, markFilled, markAvailable, reload,
}) => (
  <>
    {payout.listing_limit != null && payout.listing_count >= payout.listing_limit && (
      <div data-testid="upgrade-banner" className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-forest/25 bg-forest/[0.04] p-4">
        <div className="flex items-start gap-3">
          <Rocket className="h-5 w-5 text-forest mt-0.5 shrink-0" />
          <div>
            <p className="font-medium text-ink text-sm">You've reached your {payout.plan_tier ? payout.plan_tier.charAt(0).toUpperCase() + payout.plan_tier.slice(1) : ""} plan limit ({payout.listing_limit} listing{payout.listing_limit === 1 ? "" : "s"}).</p>
            <p className="text-sm text-mute">The more your portfolio grows, the less you pay — upgrade to add more listings and lower your platform fee.</p>
          </div>
        </div>
        <button data-testid="banner-upgrade-btn" onClick={() => setActiveTab("payouts")} className="bg-forest text-white px-4 py-2 rounded-lg font-medium text-sm hover:bg-forest-hover transition-colors shrink-0">
          Upgrade plan
        </button>
      </div>
    )}
    <div className="flex justify-between items-center mb-4 gap-3">
      <h2 className="text-xl font-heading font-medium text-ink">Your Listings</h2>
      <div className="flex items-center gap-2">
        <MigrateRentalDialog reload={reload} />
      <Dialog open={propOpen} onOpenChange={setPropOpen}>
        <DialogTrigger asChild>
          <button data-testid="add-property-btn" onClick={openCreate} className="bg-forest text-white px-4 py-2 rounded-lg font-medium hover:bg-forest-hover transition-colors flex items-center gap-2 text-sm">
            <Plus className="h-4 w-4" /> List Property
          </button>
        </DialogTrigger>
        <DialogContent className="bg-white rounded-2xl max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">{editId ? "Edit Property" : "List a New Property"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Title</Label><Input data-testid="prop-title" value={propForm.title} onChange={set("title")} className="mt-1 border-line" /></div>
            <div><Label>Description</Label><Textarea data-testid="prop-desc" value={propForm.description} onChange={set("description")} className="mt-1 border-line" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Address</Label><Input value={propForm.address} onChange={set("address")} className="mt-1 border-line" /></div>
              <div><Label>City</Label><Input data-testid="prop-city" value={propForm.city} onChange={set("city")} className="mt-1 border-line" /></div>
              <div><Label>State</Label><Input value={propForm.state} onChange={set("state")} className="mt-1 border-line" /></div>
              <div><Label>ZIP</Label><Input value={propForm.zip_code} onChange={set("zip_code")} className="mt-1 border-line" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Type</Label>
                <select value={propForm.property_type} onChange={set("property_type")} className="mt-1 w-full border border-line rounded-lg px-3 py-2 bg-white text-sm">
                  <option value="apartment">Apartment</option>
                  <option value="house">House</option>
                  <option value="condo">Condo</option>
                  <option value="townhouse">Townhouse</option>
                </select>
              </div>
              <div><Label>Square feet</Label><Input type="number" value={propForm.square_feet} onChange={set("square_feet")} className="mt-1 border-line" /></div>
              <div><Label>Bedrooms</Label><Input type="number" value={propForm.bedrooms} onChange={set("bedrooms")} className="mt-1 border-line" /></div>
              <div><Label>Bathrooms</Label><Input type="number" step="0.5" value={propForm.bathrooms} onChange={set("bathrooms")} className="mt-1 border-line" /></div>
              <div><Label>Monthly rent ($)</Label><Input data-testid="prop-rent" type="number" value={propForm.monthly_rent} onChange={set("monthly_rent")} className="mt-1 border-line" /></div>
              <div><Label>Security deposit ($)</Label><Input type="number" value={propForm.security_deposit} onChange={set("security_deposit")} className="mt-1 border-line" /></div>
            </div>
            <div><Label>Amenities (comma separated)</Label><Input value={propForm.amenities} onChange={set("amenities")} className="mt-1 border-line" placeholder="Parking, Pet Friendly, In-unit Laundry" /></div>
            <div>
              <Label>Photos</Label>
              <label htmlFor="prop-image-upload" data-testid="prop-image-upload-label" className="mt-1 flex flex-col items-center justify-center gap-1 border-2 border-dashed border-line rounded-xl py-6 cursor-pointer hover:border-forest/40 hover:bg-bone/50 transition-colors">
                <ImagePlus className="h-6 w-6 text-forest" strokeWidth={1.75} />
                <span className="text-sm text-ink font-medium">Upload photos</span>
                <span className="text-xs text-mute">JPG or PNG, up to 5MB each</span>
                <input id="prop-image-upload" data-testid="prop-image-input" type="file" accept="image/*" multiple onChange={onImageFiles} className="hidden" />
              </label>
              {propForm.images?.length > 0 && (
                <div className="mt-3 grid grid-cols-3 sm:grid-cols-4 gap-2">
                  {propForm.images.map((src, i) => (
                    <div key={src} className="relative group aspect-square rounded-lg overflow-hidden border border-line">
                      <img src={src} alt="" className="h-full w-full object-cover" />
                      <button type="button" data-testid={`remove-image-${i}`} onClick={() => removeImage(i)} className="absolute top-1 right-1 h-6 w-6 rounded-full bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="pt-1">
              <p className="text-xs text-mute">Applicants fill out your online <span className="font-medium text-ink">Application Form</span> (edit it in the Application Form tab) when they apply.</p>
            </div>
          </div>
          <DialogFooter>
            <button data-testid="save-property-btn" disabled={busy} onClick={saveProperty} className="w-full bg-forest text-white py-2.5 rounded-lg font-medium hover:bg-forest-hover transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} {editId ? "Save Changes" : "Publish Listing"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </div>

    {properties.length === 0 ? (
      <div className="bg-white border border-line rounded-xl"><EmptyState icon={Building2} title="No listings yet" subtitle="Add your first property to start receiving applications." /></div>
    ) : (
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {properties.map((p) => (
          <div key={p.id} className="bg-white border border-line rounded-xl overflow-hidden">
            <div className="relative">
              <img src={p.images?.[0] || "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?q=85"} alt="" className={`h-40 w-full object-cover ${p.hidden ? "opacity-60" : ""}`} />
              {p.hidden && (
                <span data-testid={`hidden-badge-${p.id}`} className="absolute top-2 left-2 inline-flex items-center gap-1 rounded-full bg-ink/85 text-white px-2.5 py-0.5 text-xs font-semibold">
                  <EyeOff className="h-3 w-3" /> Hidden
                </span>
              )}
              {p.filled && (
                <span data-testid={`filled-badge-${p.id}`} className="absolute top-2 right-2 inline-flex items-center gap-1 rounded-full bg-forest text-white px-2.5 py-0.5 text-xs font-semibold">
                  <CheckCircle2 className="h-3 w-3" /> Filled
                </span>
              )}
              {!p.filled && !p.available && (
                <span data-testid={`offmarket-badge-${p.id}`} className="absolute top-2 right-2 inline-flex items-center gap-1 rounded-full bg-amber-500 text-white px-2.5 py-0.5 text-xs font-semibold">
                  <RotateCcw className="h-3 w-3" /> Off-market
                </span>
              )}
            </div>
            <div className="p-4">
              <h3 className="font-heading font-medium text-ink truncate">{p.title}</h3>
              <p className="text-sm text-mute">{p.city}, {p.state}</p>
              <p className="mt-1 font-semibold text-forest">{money(p.monthly_rent)}/mo</p>
              <div className="flex gap-2 mt-3">
                <button data-testid={`edit-prop-${p.id}`} onClick={() => openEdit(p)} className="flex-1 border border-line rounded-lg py-1.5 text-sm hover:bg-bone flex items-center justify-center gap-1"><Pencil className="h-3.5 w-3.5" /> Edit</button>
                <button data-testid={`del-prop-${p.id}`} onClick={() => deleteProperty(p.id)} className="flex-1 border border-red-200 text-red-600 rounded-lg py-1.5 text-sm hover:bg-red-50 flex items-center justify-center gap-1"><Trash2 className="h-3.5 w-3.5" /> Delete</button>
              </div>
              {!p.occupied && <ShareListingDialog property={p} />}
              {p.available && !p.filled && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <button data-testid={`mark-filled-${p.id}`} className="mt-2 w-full border border-line text-ink rounded-lg py-1.5 text-sm hover:bg-bone flex items-center justify-center gap-1.5"><CheckCircle2 className="h-3.5 w-3.5 text-forest" /> Mark filled</button>
                  </AlertDialogTrigger>
                  <AlertDialogContent data-testid={`mark-filled-dialog-${p.id}`}>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Mark "{p.title}" as filled?</AlertDialogTitle>
                      <AlertDialogDescription>
                        This takes the listing off Browse and <span className="font-semibold text-ink">automatically declines all pending applicants</span> with your polite decline message. This won't issue any coupons — you can send a free-application coupon separately if you'd like to invite someone to re-apply for another listing.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel data-testid={`mark-filled-cancel-${p.id}`}>Cancel</AlertDialogCancel>
                      <AlertDialogAction data-testid={`mark-filled-confirm-${p.id}`} onClick={() => markFilled(p.id)} className="bg-forest hover:bg-forest-hover">Mark filled & decline pending</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
              {!p.available && (
                <button data-testid={`mark-available-${p.id}`} onClick={() => markAvailable(p.id)} className="mt-2 w-full border border-forest text-forest rounded-lg py-1.5 text-sm hover:bg-forest/5 flex items-center justify-center gap-1.5"><RotateCcw className="h-3.5 w-3.5" /> Re-list (mark available)</button>
              )}
              {p.hidden ? (
                <button data-testid={`unhide-prop-${p.id}`} onClick={() => toggleVisibility(p)} className="mt-2 w-full border border-forest text-forest rounded-lg py-1.5 text-sm hover:bg-forest/5 flex items-center justify-center gap-1.5"><Eye className="h-3.5 w-3.5" /> Unhide (show on Browse)</button>
              ) : p.has_signed_lease ? (
                <button data-testid={`hide-prop-${p.id}`} onClick={() => toggleVisibility(p)} className="mt-2 w-full border border-line text-ink rounded-lg py-1.5 text-sm hover:bg-bone flex items-center justify-center gap-1.5"><EyeOff className="h-3.5 w-3.5" /> Hide from Browse</button>
              ) : (
                <p className="mt-2 text-xs text-mute text-center">Hide unlocks once a lease is signed.</p>
              )}
            </div>
          </div>
        ))}
      </div>
    )}
  </>
);
